import { ConsentDisplay } from '@/components/consent-display';
import { Heading } from '@/components/heading';
import { ParentalConsentImage } from '@/components/registration/consent-step';
import { GeneralErrorDialog } from '@/components/registration/general-error-dialog';
import { MaxColumnsContainer } from '@/components/ui/layout';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { Box, Button, Theme } from '@/libs/material';
import { routes } from '@/utils/routes';
import { EnumScreenName, EventProperties } from '@calm/evexia-analytics';
import { AccountConsent } from '@calm/evexia-web-sdk';
import {
    BackButtonView,
    styleBackButtonPositioning,
} from '@rhg/components/back-button';
import consentStepMessages from '@rhg/components/registration/consent-step/messages';
import { useGetCircles } from '@rhg/hooks/use-circles';
import { ConsentRequirement } from '@rhg/hooks/use-consent-context/types';
import useHasAcceptedCohortTos from '@rhg/hooks/use-has-accepted-tos';
import { useHistory } from '@rhg/hooks/use-history';
import { useMostRecentSponsorship } from '@rhg/hooks/use-most-recent-sponsorship';
import { useUpdateConsent } from '@rhg/hooks/use-update-consent';
import { logger } from '@rhg/libs/logger';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import {
    styleButtonGroup,
    styleContainer,
    styleFlexGrow,
    styleFooterActions,
    styleOuterWrapper,
} from '../styles';

const styleHeading = {
    width: '100%',
    mb: (theme: Theme) => theme.ds.spacingUnits.SECTION,
    px: (theme: Theme) => ({
        xs: theme.ds.spacingUnits.SIDE_SMALL,
        md: theme.ds.spacingUnits.SIDE_MEDIUM,
        lg: theme.ds.spacingUnits.SIDE_LARGE,
    }),
};

const styleConsentContainer = { height: '100%' };

interface Props {
    onClose: () => void;
    consentRequirements: ConsentRequirement;
    accountConsents: AccountConsent[];
    onBack?: () => void;
    trackScreen?: EnumScreenName;
    trackProps?: EventProperties;
}

export function StandaloneParentalConsent({
    onClose,
    consentRequirements,
    accountConsents,
    onBack,
    trackScreen,
    trackProps,
}: Props) {
    const { formatMessage } = useIntl();
    const { getCirclesAsync } = useGetCircles();
    const { updateConsentAsync } = useUpdateConsent();
    const { push } = useRouter();
    const { refetch: refetchConsent } = useHasAcceptedCohortTos();
    const { refetch: refetchMostRecentSponsorship } =
        useMostRecentSponsorship();
    const [error, setError] = useState(false);
    const { back } = useHistory();
    const isMobile = useIsMobile();

    const onAccept = async () => {
        try {
            await updateConsentAsync({
                consentContext: consentRequirements.parental,
                accountConsentId: accountConsents[0].consentId,
            });
            await refetchConsent();
            await refetchMostRecentSponsorship();
            await getCirclesAsync(true);
            back(routes.profile);
            onClose();
        } catch (_error) {
            setError(true);
            logger.error(
                'Error from accepting parental consent',
                {},
                _error as Error
            );
        }
    };

    return (
        <Box sx={styleOuterWrapper}>
            <Box sx={styleContainer}>
                <Heading
                    sx={styleHeading}
                    title={formatMessage(
                        consentStepMessages.parentalConsentHeading
                    )}
                    titleAlign="center"
                    titleVariant="h5"
                    leftButton={
                        onBack && (
                            <BackButtonView
                                onClick={onBack}
                                trackButton="Back"
                                trackScreen={trackScreen}
                                trackProps={trackProps}
                                sx={styleBackButtonPositioning}
                            />
                        )
                    }
                />
                <MaxColumnsContainer columns={8} sx={styleConsentContainer}>
                    <ConsentDisplay
                        consentContext={consentRequirements.parental}
                        title={formatMessage(
                            consentStepMessages.parentalConsentHeading
                        )}
                        imageOverride={<ParentalConsentImage />}
                        srTitle={formatMessage(
                            consentStepMessages.parentalConsentHeading
                        )}
                        fallbackUrl={routes.profile}
                    />
                </MaxColumnsContainer>
                {error && (
                    <GeneralErrorDialog
                        onClose={() => {
                            push(routes.profile).catch((err) =>
                                logger.error(
                                    'Failed to navigate to profile',
                                    {},
                                    err as Error
                                )
                            );
                        }}
                        showSupportEmail={true}
                        showSupportMessage={true}
                    />
                )}
            </Box>
            <Box sx={styleFooterActions}>
                <Box sx={styleButtonGroup}>
                    <Button
                        color="primary"
                        variant="contained"
                        trackButton="Accept"
                        trackScreen={trackScreen}
                        trackProps={trackProps}
                        // TODO(EVE-10506): failure logs to Datadog only; needs user-facing error UX
                        onClick={() => {
                            onAccept().catch((err) =>
                                logger.error(
                                    'Failed to accept parental consent',
                                    {},
                                    err as Error
                                )
                            );
                        }}
                        sx={isMobile ? {} : styleFlexGrow}
                    >
                        {formatMessage(consentStepMessages.accept)}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
